import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts } from "../../../Styles";
import { CheckBox, InputField, SelectField } from "../../BaseComponents";
import { Value } from "baseui/select";
import { Pen, Section } from "./newSectionModal";

interface NewSectionModalLvl1_Props {
  pens: Pen[];
  onPensChange: (updatedPens: Pen[]) => void;
  onSectionChange: (updatedSection: Section) => void;
}

const NewSectionModalLvl1: React.FC<NewSectionModalLvl1_Props> = ({pens, onPensChange, onSectionChange}) => {
  const {t} = useTranslation()
  const [rows, setRows] = useState<number>(7);
  const [columns, setColumns] = useState<number>(2);
  const [startingNumber, setStartingNumber] = useState<number>(101);
  const [direction, setDirection] = useState<number>(0);
  const [order, setOrder] = useState<number>(0);
  const [disabledPens, setDisabledPens] = useState<string[]>([]);
  const [checked, setChecked] = useState({feederOnly: true, water: false, activator: false})

  const directionOptions = [    
    { label: t("locationFeeder.newSectionPopup.horizontal"), id: 0 },
    { label: t("locationFeeder.newSectionPopup.vertical"), id: 1 },
  ]

  const orderOptions = [    
    { label: t("locationFeeder.newSectionPopup.asc"), id: 0 },
    { label: t("locationFeeder.newSectionPopup.desc"), id: 1 },
  ]

  const findLabelById = (id: string | number, options: { label: string; id: number }[]) => {
    const option = options.find((option) =>
      typeof id === "string"
        ? option.label.toLowerCase() === id.toLowerCase()
        : option.id === id
    );
    return option ? option.label : "";
  };

  // Number of active pens
  const activePensCount = pens.filter((pen) => pen.penId !== null).length;

  // #region | Pen Update
  const updatePens = (rows: number, columns: number, startingNumber: number, direction: number) => {
    const newPens: Pen[] = [];
    let counter = startingNumber;
    const isAscending = order === 0;

    if (direction === 0) {
      for (let i = 0; i < rows; i++) {
        for (let j = 0; j < columns; j++) {
          const id = `${j + 1}:${i + 1}`;
          const penId = disabledPens.includes(id) ? null : counter.toString();
          newPens.push({ id, penId, io: null });
          if (!disabledPens.includes(id)) counter += isAscending ? 1 : -1;
        }
      }
    } else {
      for (let j = 0; j < columns; j++) {
        for (let i = 0; i < rows; i++) {
          const id = `${j + 1}:${i + 1}`;
          const penId = disabledPens.includes(id) ? null : counter.toString();
          newPens.push({ id, penId, io: null });
          if (!disabledPens.includes(id)) counter += isAscending ? 1 : -1;
        }
      }
    }

    onPensChange(newPens);
  };

  const handleRowChange = (e: string) => {
    const newRows = parseInt(e, 10) || 0;
    setRows(newRows);
    updatePens(newRows, columns, startingNumber, direction);
  };

  const handleColumnChange = (e: string) => {
    const newColumns = Math.min(parseInt(e, 10) || 0, 2);
    setColumns(newColumns);
    updatePens(rows, newColumns, startingNumber, direction);
  };

  const handleStartingNumberChange = (e: string) => {
    const newStartingNumber = parseInt(e, 10) || 0;
    setStartingNumber(newStartingNumber);
    updatePens(rows, columns, newStartingNumber, direction);
  };

  const handleDirectionChange = (e: Value) => {
    const selectedLabel = e[0]?.id;
    if (typeof selectedLabel === "number") {
      const newDirection = selectedLabel;
      setDirection(newDirection);
      updatePens(rows, columns, startingNumber, newDirection);
    }
  };

  const handleOrderChange = (e: Value) => {
    const selectedLabel = e[0]?.id;
    if (typeof selectedLabel === "number") {
      setOrder(selectedLabel);
      updatePens(rows, columns, startingNumber, direction);
    }
  };

  const handleCellToggle = (rowIndex: number, colIndex: number) => {
    const penId = `${colIndex + 1}:${rowIndex + 1}`;
    setDisabledPens((prev) =>
      prev.includes(penId)
        ? prev.filter((id) => id !== penId)
        : [...prev, penId]
    );
  };

  useEffect(() => {
    updatePens(rows, columns, startingNumber, direction);
  }, [rows, columns, startingNumber, direction, order, disabledPens]);
  // #endregion

  // #region | TableView
  const tableView = (
    <div style={{ borderRadius: "10px", overflow: "hidden", width: "fit-content" }}>
      <table style={{ borderSpacing: "10px", margin: "-10px" }}>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columns }).map((_, colIndex) => {
                const pen = pens.find(
                  (pen) => pen.id === `${colIndex + 1}:${rowIndex + 1}`
                );
                return (
                  <td
                    key={colIndex}
                    style={{
                      width: "80px",
                      height: "30px",
                      textAlign: "center",
                      alignContent: "center",
                      backgroundColor: colors.greyLightSecondary,
                      filter: disabledPens.includes(pen?.id || "")
                        ? "opacity(0.5)"
                        : "opacity(1)",
                      transition: "filter 0.3s",
                      color: colors.greyDarkPrimary,
                      ...fonts.paragraph,
                      ...fonts.fontSize4,
                      cursor: "pointer",
                    }}
                    onClick={() => handleCellToggle(rowIndex, colIndex)}
                  >
                    {pen?.penId || ""}
                  </td> 
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  // #endregion

  // #region | Pen Content / Handle Checkboxes
  // Function to update the section data when checkboxes are changed
  const updateSectionData = () => {
    const updatedSection: Section = { pens, sectionNo: 1, lanController: "AICO_LAN1", feeder: checked.feederOnly, water: checked.water, activator: checked.activator };

    if (updatedSection.feeder) {
      updatedSection.pens.forEach(pen => pen.feederOutput = null);
    }
    if (updatedSection.water) {
      updatedSection.pens.forEach(pen => pen.waterOutput = null);
    }
    if (updatedSection.activator) {
      updatedSection.pens.forEach(pen => pen.activatorInput = null);
    }

    onSectionChange(updatedSection);  
  };

  useEffect(() => {
    updateSectionData(); 
  }, [checked, pens]);  

  const handleCheckboxChange = (name: keyof typeof checked) => {
    setChecked((prevState) => {
      if (name === 'feederOnly') {
        return {
          feederOnly: !prevState.feederOnly,
          water: false,
          activator: false,
        };
      } else {
        return {
          ...prevState,
          [name]: !prevState[name],
          feederOnly: false,
        };
      }
    });
  };
  // #endregion
  
  return (
    <div style={{ width: "600px" }}>

      <div style={{
          borderTop: "2px solid " + colors.blueDarkSecondary,
          padding: "10px 10px",
          maxHeight: "40vh",
          overflowY: "scroll",
          overflowX: "hidden",
          scrollbarWidth: "thin",
          scrollbarColor: colors.blueDarkSecondary+colors.transparent,
          gap: "10px",
          display: "grid",
          gridTemplateColumns: "350px 1fr",
          gridTemplateRows: "1fr",
      }}>

        {/* Create Table View */}
        <div style={{ gridArea: "1/1/2/2", display: "flex", flexDirection: "column", gap: "15px" }}>
          {/* Columns */}
          <div>
            <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("locationFeeder.newSectionPopup.columns")}</p>
            <InputField textStart={true} inputType="number" context={columns} onChange={handleColumnChange} maxValue={2} minValue={1}/>
          </div>
          {/* Rows */}
          <div>
            <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("locationFeeder.newSectionPopup.rows")}</p>
            <InputField textStart={true} inputType="number" context={rows} onChange={handleRowChange} minValue={1}/>
          </div>
          {/* First Pen No. */}
          <div>
            <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("locationFeeder.newSectionPopup.firstPenNo")}</p>
            <InputField textStart={true} inputType="number" context={startingNumber} onChange={handleStartingNumberChange} minValue={1}/>
          </div>
          {/* Order */}
          <div>
            <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("locationFeeder.newSectionPopup.Order")}</p>
            <SelectField 
              overrides={{
                textStart: true
              }}
              options={orderOptions}
              defaultValue={findLabelById(order, orderOptions)}
              onChange={handleOrderChange}
            />
          </div>
          {/* Direction */}
          <div>
            <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("locationFeeder.newSectionPopup.direction")}</p>
            <SelectField 
              overrides={{
                textStart: true
              }} 
              options={directionOptions}
              defaultValue={findLabelById(direction, directionOptions)}
              onChange={handleDirectionChange}
            />
          </div>
        </div>

        {/* Table View */}
        <div style={{ gridArea: "1/2/2/3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "start", gap: "5px"}}>
          <div style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"}}>         
            <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{t("locationFeeder.newSectionPopup.numberOfPens")+":"}</p>
            <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{activePensCount}</p>
          </div>
          {tableView}
          <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, textAlign: "center", whiteSpace: 'pre-wrap', maxWidth: "150px"}}>{t("locationFeeder.newSectionPopup.tableViewDisclaimer")}</p>
        </div>

      </div>
      
      {/* Pen Content */}
      <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("locationFeeder.newSectionPopup.penContent")}</p>
      <div style={{borderTop: "2px solid"+colors.blueDarkSecondary, borderBottom: "2px solid"+colors.blueDarkSecondary, padding: "10px 0px", maxHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{width: "fit-content", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", columnGap: "100px", rowGap: "10px"}}>
          <CheckBox text={t("locationFeeder.newSectionPopup.feederOnly")} isChecked={checked.feederOnly} onChange={() => handleCheckboxChange("feederOnly")}/> 
          <CheckBox text={t("locationFeeder.newSectionPopup.water")} isChecked={checked.water} onChange={() => handleCheckboxChange("water")}/> 
          <CheckBox text={t("locationFeeder.newSectionPopup.activator")} isChecked={checked.activator} onChange={() => handleCheckboxChange("activator")}/> 
        </div>
      </div>

    </div>
  );
}

export default NewSectionModalLvl1


